import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#232323] flex items-center justify-center px-6 relative overflow-hidden">
      {/* Glow effects */}
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#6B5CE7] blur-[150px] opacity-10"></div>
      <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#4ECDC4] blur-[150px] opacity-10"></div>
      
      {/* Warm gradient rule at top */}
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#E040FB] via-[#FF6B35] to-[#FFD93D]"></div>

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img src="/logo-light.png" alt="Players Health" className="h-10" />
          </div>
          <h1 className="text-white font-black text-2xl uppercase tracking-wide" style={{fontFamily: "'Barlow Condensed', sans-serif"}}>
            Admin Portal
          </h1>
          <p className="text-white/50 mt-2 text-sm" style={{fontFamily: "'Inter', sans-serif", textTransform: 'none', fontWeight: 400}}>
            Sign in to manage applications and quotes
          </p>
        </div>

        <Card className="bg-white/5 backdrop-blur-sm border border-white/10 shadow-2xl">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-white/70 font-medium text-sm" style={{fontFamily: "'Inter', sans-serif", textTransform: 'none', fontWeight: 500, letterSpacing: 'normal'}}>Email</Label>
                <Input
                  id="email"
                  data-testid="admin-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-11 mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus:border-[#7B6FE8] focus:ring-[#7B6FE8]"
                  placeholder="admin@playershealth.com"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-white/70 font-medium text-sm" style={{fontFamily: "'Inter', sans-serif", textTransform: 'none', fontWeight: 500, letterSpacing: 'normal'}}>Password</Label>
                <Input
                  id="password"
                  data-testid="admin-password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="h-11 mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus:border-[#7B6FE8] focus:ring-[#7B6FE8]"
                  placeholder="Enter password"
                />
              </div>

              <Button
                type="submit"
                data-testid="admin-login-btn"
                disabled={loading}
                className="w-full h-11 rounded-lg font-bold text-sm uppercase tracking-wider text-white"
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  background: 'linear-gradient(135deg, #6B5CE7, #4ECDC4)',
                }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Gradient rule at bottom of card */}
        <div className="mt-6 h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] rounded-full opacity-40"></div>
      </div>
    </div>
  );
};

export default AdminLogin;
